import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import SaveIndicator from "../components/SaveIndicator";
import { useEdit } from "../context/EditContext";
import { useUserPreferences } from '../../core/context/UserPreferencesContext';
import { generateEditUrl } from '../../core/utils/urlUtils';
import useMatchMedia from "../../core/hooks/useMatchMedia";

import PageNavigationEditor from "./PageNavigationEditor";
import NavigationManagement from "./NavigationManagement";
import VersionManagement from "./VersionManagement";
import DevicePreviewControls from "./DevicePreviewControls";
import EditSettingsDropdown from "./EditSettingsDropdown";
import FeedbackModal from "./FeedbackModal";
import PublishButton from "./PublishButton";
import { VersionIcon, SparklesIcon, PaintPaletteIcon, FormatPainterIcon } from "./icons";
import { HomeIcon, ExternalLinkIcon, FeedbackIcon, InfoIcon, SettingsDotsIcon } from "../../design-system/icons";
import { ThemeToggle } from "../../design-system";
import { typography, surfaces, borders, textColors, brandColors, interactive } from "../../design-system/tokens";
import { API_ENDPOINTS } from "./constants";
import { usePreview } from "../context/PreviewContext";
import { useEditUI } from "../context/EditUIPreferencesContext";
import { Z_INDEX } from '../../core/utils/zIndexScale';
import { APP_DOMAIN, APP_URL_WWW } from '../../core/utils/config';
import { getCurrentPage, formatVersionName, formatLastEditedTime } from "./utils";
import { usePublishState } from "./hooks/usePublishState";
import { usePageCreation } from "./hooks/usePageCreation";
import SubscriptionPlansModal from "../../subscription/SubscriptionPlansModal";
import WebsiteDefaultsModal from "../defaults/WebsiteDefaultsModal";
import { TutorialStartPrompt, useEditorTutorial } from "../tutorial";
import { TUTORIAL_TARGETS } from "../tutorial/constants";
import { ArrowUturnLeftIcon, ArrowUturnRightIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";
import { WEBSITE_CREATION_SHOWCASE_PROMPT_DELAY_MS } from "../showcase/creationShowcaseTiming";

// Temporarily hide the editor settings (three-dots) control per product request.
const SHOW_EDITOR_SETTINGS = false;

const HoverTooltip = ({ label, children, placement = "bottom" }) => {
  if (!label) return children;

  const isTop = placement === "top";
  const containerPosition = isTop
    ? "bottom-full mb-2"
    : "top-full mt-2";
  const arrowPosition = isTop ? "-bottom-1" : "-top-1";

  return (
    <span className="relative inline-flex group">
      {children}
      <span
        role="tooltip"
        className={[
          "pointer-events-none absolute left-1/2 -translate-x-1/2",
          containerPosition,
          "opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0",
          "transition-all duration-150 ease-out",
        ].join(" ")}
        style={{ zIndex: Z_INDEX.POPOVER }}
      >
        <span
          className={[
            "relative inline-flex items-center",
            "px-3 py-1.5 rounded-lg",
            "bg-gray-900/90 dark:bg-dark-900/95",
            "border border-white/10 dark:border-white/10",
            "shadow-xl backdrop-blur-sm",
            "text-white",
            typography.caption,
            "whitespace-nowrap",
          ].join(" ")}
        >
          <span
            className={[
              "absolute left-1/2 -translate-x-1/2",
              arrowPosition,
              "w-2.5 h-2.5 rotate-45",
              "bg-gray-900/90 dark:bg-dark-900/95",
              "border border-white/10 dark:border-white/10",
            ].join(" ")}
            aria-hidden="true"
          />
          {label}
        </span>
      </span>
    </span>
  );
};

const EditNavBar = ({ username, domain, version_slug, pages = [], websiteId, website }) => {
  const {
    groupViewMode,
    setGroupViewMode,
    dockLayout,
    setDockLayout,
    editPanelView,
    setEditPanelView,
    isTablet,
  } = useEditUI();
  // Navbar layout breakpoint: treat <=1180px as "compact" so the tool buttons use the compact format
  // as the layout becomes tight (and as the website identity hides).
  const isCompactNav = useMatchMedia("(max-width: 1180px)", isTablet);
  // Hide website identity block on narrow desktops to preserve space.
  const hideWebsiteIdentity = useMatchMedia("(max-width: 1180px)", false);
  // True mobile (phone-sized) viewport — used for the post-creation "best on desktop or iPad" notice.
  const isMobileViewport = useMatchMedia("(max-width: 767px)", false);
  // Device preview "view mode" breakpoint: use compact controls earlier (up to ~1630px)
  // so medium desktops get the compact view-mode UI even while the navbar is still desktop.
  const useCompactViewControls = useMatchMedia("(max-width: 1630px)", isCompactNav);
  // Only force full page carousel on wide desktops. Below `xl`, let `PageNavigationEditor` use its compact layout.
  const forceFullPageNav = useMatchMedia("(min-width: 1280px)", false);
  const location = useLocation();
  const {
    saveStatus,
    selectElement,
    selectedElement,
    exitAddMode,
    undo,
    redo,
    canUndo,
    canRedo,
    formatPainter,
    armFormatPainter,
    clearFormatPainter,
    isFormatPainterEligible,
  } = useEdit();

  // The painter button reflects three states:
  //   * disabled : nothing eligible to copy from
  //   * idle     : an eligible source is selected; click to arm
  //   * armed    : painter is currently active (one-shot or sticky)
  const formatPainterCanArm = isFormatPainterEligible
    ? isFormatPainterEligible(selectedElement)
    : false;
  const formatPainterArmed = !!formatPainter?.active;
  const formatPainterSticky = !!formatPainter?.sticky;

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const root = document.documentElement;
    if (!root) return undefined;

    if (formatPainterArmed) {
      root.setAttribute("data-format-painter-active", "1");
      root.setAttribute("data-format-painter-sticky", formatPainterSticky ? "1" : "0");
    } else {
      root.removeAttribute("data-format-painter-active");
      root.removeAttribute("data-format-painter-sticky");
    }

    return () => {
      root.removeAttribute("data-format-painter-active");
      root.removeAttribute("data-format-painter-sticky");
    };
  }, [formatPainterArmed, formatPainterSticky]);

  // Single-click arms one-shot immediately; double-click upgrades to sticky.
  // The browser sends a second click before dblclick, so ignore multi-click
  // click events here and let handleFormatPainterDoubleClick own that path.
  const handleFormatPainterClick = (event) => {
    if (event?.detail > 1) return;
    if (formatPainterArmed) {
      // Clicking the toolbar button while armed always disarms (Office parity).
      clearFormatPainter?.();
      return;
    }
    if (!formatPainterCanArm) return;
    armFormatPainter?.(selectedElement, { sticky: false });
  };

  const handleFormatPainterDoubleClick = () => {
    if (formatPainterArmed) {
      // Double-click upgrades a one-shot to sticky, or disarms if already sticky.
      if (formatPainterSticky) {
        clearFormatPainter?.();
      } else if (selectedElement && isFormatPainterEligible?.(selectedElement)) {
        armFormatPainter?.(selectedElement, { sticky: true });
      } else if (formatPainter?.source) {
        // Re-arm sticky using the existing source snapshot.
        armFormatPainter?.(formatPainter.source, { sticky: true });
      }
      return;
    }
    if (!formatPainterCanArm) return;
    armFormatPainter?.(selectedElement, { sticky: true });
  };
  const { preferences } = useUserPreferences();
  const tutorial = useEditorTutorial();
  const startTutorial = tutorial.start;
  const [showTutorialPrompt, setShowTutorialPrompt] = useState(false);
  const [navManagementDropdownOpen, setNavManagementDropdownOpen] = useState(false);
  const [versionManagementDropdownOpen, setVersionManagementDropdownOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [isManagingNav, setIsManagingNav] = useState(false);
  const [navManagementError, setNavManagementError] = useState('');
  const [defaultsModalOpen, setDefaultsModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const toolsPanelRef = useRef(null);

  const tutorialAutoExpandedRef = useRef(false);
  const tutorialTargetId = tutorial?.currentStep?.targetId || null;
  const tutorialWantsToolsExpanded = Boolean(
    isCompactNav &&
      tutorial?.isOpen &&
      tutorialTargetId === TUTORIAL_TARGETS.NAVBAR_TOOLS,
  );

  // Tutorial integration: ensure the tools panel is expanded when a step targets an element inside it.
  // We only auto-close if we were the one who auto-opened it.
  useEffect(() => {
    if (!tutorialWantsToolsExpanded) {
      if (tutorialAutoExpandedRef.current) {
        tutorialAutoExpandedRef.current = false;
        setToolsExpanded(false);
      }
      return;
    }

    if (!toolsExpanded) {
      tutorialAutoExpandedRef.current = true;
      setToolsExpanded(true);
    }
  }, [tutorialWantsToolsExpanded, toolsExpanded]);

  // Desktop: we don't use the expandable tools panel. If the viewport grows beyond tablet
  // while it's open, close it so it doesn't affect desktop rendering/state.
  useEffect(() => {
    if (isCompactNav) return;
    if (toolsExpanded) setToolsExpanded(false);
    tutorialAutoExpandedRef.current = false;
  }, [isCompactNav, toolsExpanded]);

  const currentPage = getCurrentPage(pages, location, username, domain, version_slug);
  const queryClient = useQueryClient();

  // The `pages` prop is fetched once by EditWebsitePage and is NOT refreshed when the user
  // edits the current page (background colors/pattern, layout, etc.) through the EditPanel.
  // The autosave path invalidates the page-skeleton React Query cache (see EditContext),
  // which keeps the visible WebsitePage in sync — but the navbar's `pages` prop stays stale.
  //
  // When opening the page settings panel we always want to seed `selectedElement` with the
  // freshest available page data so background colors/pattern reflect what's actually on
  // screen. Read from the page-skeleton cache first and fall back to `currentPage` if the
  // cache hasn't been populated yet (e.g., the user just landed on the editor).
  const buildPageSelectionPayload = useCallback(() => {
    if (!currentPage) return null;
    const subdomainKey = currentPage.subdomain || "main";
    const cached = queryClient.getQueryData([
      "page-skeleton",
      websiteId,
      subdomainKey,
    ]);
    const sourcePage =
      cached && typeof cached === "object" && cached.id === currentPage.id
        ? cached
        : currentPage;

    return {
      ...sourcePage,
      type: "website_page",
      website: websiteId,
    };
  }, [currentPage, queryClient, websiteId]);

  const animationsPanelOpen = editPanelView === "animations";
  const handleToggleAnimationsPanel = () => {
    if (animationsPanelOpen) {
      setEditPanelView("elements");
      return;
    }
    // Opening website-level animation controls should dismiss any active add-element workflow.
    exitAddMode?.();
    setEditPanelView("animations");
  };

  // If the user is currently editing page-level settings and navigates to a different page,
  // automatically switch the edit panel to the newly viewed page.
  useEffect(() => {
    if (!currentPage || !selectElement) return;
    if (selectedElement?.type !== "website_page") return;
    if (selectedElement?.id === currentPage.id) return;

    const payload = buildPageSelectionPayload();
    if (!payload) return;

    selectElement(payload);
  }, [
    currentPage,
    websiteId,
    selectedElement?.type,
    selectedElement?.id,
    selectElement,
    buildPageSelectionPayload,
  ]);

  // Show the prompt during the final blast so it feels responsive.
  const tutorialPromptDelayMs = WEBSITE_CREATION_SHOWCASE_PROMPT_DELAY_MS;

  const tutorialPromptFallbackKey = `editor:tutorialPromptDismissed:edit:${username}:${domain}:${version_slug}`;
  const tutorialPromptWebsiteKey = websiteId
    ? `editor:tutorialPromptDismissed:websiteId:${websiteId}`
    : null;

  // Mobile-only "best on desktop or iPad" notice shown right before the tutorial prompt.
  const mobileNoticeFallbackKey = `editor:mobileEditorNoticeDismissed:edit:${username}:${domain}:${version_slug}`;
  const mobileNoticeWebsiteKey = websiteId
    ? `editor:mobileEditorNoticeDismissed:websiteId:${websiteId}`
    : null;
  const [mobileNoticeOpen, setMobileNoticeOpen] = useState(false);
  // Default true so the tutorial prompt is never blocked on desktop / when no notice is needed.
  const [mobileNoticeAcknowledged, setMobileNoticeAcknowledged] = useState(true);

  const tutorialPromptTimerRef = useRef(null);
  const clearTutorialPromptTimer = () => {
    if (!tutorialPromptTimerRef.current) return;
    try {
      window.clearTimeout(tutorialPromptTimerRef.current);
    } catch (_) {}
    tutorialPromptTimerRef.current = null;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const shouldPrompt = params.get("tutorial_prompt") === "1";

    if (!shouldPrompt) {
      clearTutorialPromptTimer();
      setShowTutorialPrompt(false);
      return;
    }

    // On mobile, hold the tutorial prompt back until the "best edited on desktop/iPad"
    // notice has been acknowledged, so the user reads it before the tour offer appears.
    if (!mobileNoticeAcknowledged) {
      clearTutorialPromptTimer();
      setShowTutorialPrompt(false);
      return;
    }

    try {
      const dismissed =
        window.localStorage.getItem(tutorialPromptFallbackKey) === "1" ||
        (tutorialPromptWebsiteKey ? window.localStorage.getItem(tutorialPromptWebsiteKey) === "1" : false);
      if (dismissed) {
        clearTutorialPromptTimer();
        setShowTutorialPrompt(false);
        return;
      }

      // Delay the prompt so the website "showcase" can play first.
      if (showTutorialPrompt) return;
      if (tutorialPromptTimerRef.current) return;
      tutorialPromptTimerRef.current = window.setTimeout(() => {
        tutorialPromptTimerRef.current = null;
        setShowTutorialPrompt(true);
      }, tutorialPromptDelayMs);
    } catch (e) {
      // If localStorage is unavailable (privacy mode), fall back to showing the prompt.
      if (showTutorialPrompt) return;
      if (tutorialPromptTimerRef.current) return;
      tutorialPromptTimerRef.current = window.setTimeout(() => {
        tutorialPromptTimerRef.current = null;
        setShowTutorialPrompt(true);
      }, tutorialPromptDelayMs);
    }
  }, [
    location.search,
    showTutorialPrompt,
    tutorialPromptDelayMs,
    tutorialPromptFallbackKey,
    tutorialPromptWebsiteKey,
    mobileNoticeAcknowledged,
  ]);

  // Decide whether to show the mobile-only "best on desktop or iPad" notice.
  // Shown only when the user lands on the editor right after creation (?tutorial_prompt=1)
  // from a true mobile viewport, and only once per website.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const shouldPrompt = params.get("tutorial_prompt") === "1";

    if (!shouldPrompt || !isMobileViewport) {
      setMobileNoticeOpen(false);
      setMobileNoticeAcknowledged(true);
      return;
    }

    try {
      const dismissed =
        window.localStorage.getItem(mobileNoticeFallbackKey) === "1" ||
        (mobileNoticeWebsiteKey ? window.localStorage.getItem(mobileNoticeWebsiteKey) === "1" : false);
      if (dismissed) {
        setMobileNoticeOpen(false);
        setMobileNoticeAcknowledged(true);
        return;
      }
    } catch (_) {
      // If localStorage is unavailable, still show the notice this session.
    }

    setMobileNoticeAcknowledged(false);
    setMobileNoticeOpen(true);
  }, [location.search, isMobileViewport, mobileNoticeFallbackKey, mobileNoticeWebsiteKey]);

  const dismissMobileNotice = () => {
    try {
      window.localStorage.setItem(mobileNoticeFallbackKey, "1");
      if (mobileNoticeWebsiteKey) {
        window.localStorage.setItem(mobileNoticeWebsiteKey, "1");
      }
    } catch (_) {
      // Ignore persistence errors; the notice will still close for this session.
    }
    setMobileNoticeOpen(false);
    setMobileNoticeAcknowledged(true);
  };

  // Backward compatibility: if an older session dismissed only the websiteId-scoped key,
  // hide/cancel promptly once websiteId is available.
  useEffect(() => {
    if (!tutorialPromptWebsiteKey) return;
    try {
      const dismissed = window.localStorage.getItem(tutorialPromptWebsiteKey) === "1";
      if (!dismissed) return;
      clearTutorialPromptTimer();
      setShowTutorialPrompt(false);
    } catch (_) {}
  }, [tutorialPromptWebsiteKey]);

  const dismissTutorialPrompt = () => {
    try {
      // Write both keys so we don't re-show if websiteId resolves later.
      window.localStorage.setItem(tutorialPromptFallbackKey, "1");
      if (tutorialPromptWebsiteKey) {
        window.localStorage.setItem(tutorialPromptWebsiteKey, "1");
      }
    } catch (e) {
      // Ignore persistence errors; dismiss for this session at least.
    }
    clearTutorialPromptTimer();
    setShowTutorialPrompt(false);
  };

  // Dim the editor background (website preview + side panels) while the start prompt is visible
  // to draw attention to the question.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (!root) return;
    if (showTutorialPrompt) {
      root.setAttribute("data-editor-tutorial-prompt-open", "1");
    } else {
      root.removeAttribute("data-editor-tutorial-prompt-open");
    }
    return () => {
      root.removeAttribute("data-editor-tutorial-prompt-open");
    };
  }, [showTutorialPrompt]);

  // Close tools panel when clicking outside
  useEffect(() => {
    if (!toolsExpanded) return;
    const handleClickOutside = (event) => {
      if (tutorialWantsToolsExpanded) return;
      if (toolsPanelRef.current && !toolsPanelRef.current.contains(event.target)) {
        setToolsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [toolsExpanded, tutorialWantsToolsExpanded]);

  // Preview controls
  const {
    mode,
    setMode,
    orientation,
    setOrientation,
    showFrame,
    setShowFrame,
  } = usePreview();

  // Publish state management
  const {
    publishConfirmOpen,
    setPublishConfirmOpen,
    publishing,
    hasPublished,
    handlePublish,
    confirmPublish,
    upgradeModalOpen,
    setUpgradeModalOpen,
    publishLimitInfo,
  } = usePublishState(website, websiteId);

  // Page creation management
  const {
    createPageData,
    setCreatePageData,
    isCreatingPage,
    createPageError,
    setCreatePageError,
    hasNavbar,
    hasLeftSidebar,
    hasRightSidebar,
    handleCreatePageSubmit,
    handleCreatePageCancel
  } = usePageCreation(website, websiteId, username, domain, version_slug);

  const hasFooter = true; // Footer is always available

  const desktopTooltipPlacement = isCompactNav ? "top" : "bottom";
  const undoRedoTooltipPlacement = "bottom";
  const showWebsiteIdentity = !isCompactNav && !hideWebsiteIdentity;

  const renderFormatPainter = () => (
    <HoverTooltip
      label={
        formatPainterArmed
          ? formatPainterSticky
            ? "Sticky painter: Esc to exit"
            : "Click an item to apply"
          : formatPainterCanArm
            ? "Format painter"
            : "Select an item first"
      }
      placement="bottom"
    >
      <button
        type="button"
        onClick={handleFormatPainterClick}
        onDoubleClick={handleFormatPainterDoubleClick}
        disabled={!formatPainterArmed && !formatPainterCanArm}
        className={[
          "relative p-1.5 lg:p-1.5 text-white rounded-lg transition-all",
          formatPainterArmed
            ? formatPainterSticky
              ? "bg-amber-400/45 ring-2 ring-amber-100 shadow-lg shadow-amber-500/30 animate-pulse cursor-copy"
              : "bg-white/30 ring-2 ring-white/60 shadow-lg shadow-white/20 animate-pulse cursor-copy"
            : "hover:bg-white/10",
          "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent",
        ].join(" ")}
        aria-label="Format painter"
        aria-pressed={formatPainterArmed}
        data-format-painter-active={formatPainterArmed ? "1" : "0"}
        data-format-painter-sticky={formatPainterSticky ? "1" : "0"}
      >
        {formatPainterArmed ? (
          <span
            className={[
              "pointer-events-none absolute inset-0 rounded-lg",
              formatPainterSticky ? "bg-amber-200/35" : "bg-white/25",
              "animate-ping",
            ].join(" ")}
            aria-hidden="true"
          />
        ) : null}
        <FormatPainterIcon className="relative z-10 w-4 h-4 lg:w-[18px] lg:h-[18px]" />
      </button>
    </HoverTooltip>
  );

  const renderUndoRedo = ({ includeFormatPainter = true } = {}) => (
    <div data-tutorial-id="navbar-undo-redo" className="flex items-center gap-1.5 flex-shrink-0">
      <HoverTooltip label={`Undo (⌘/Ctrl+Z)`} placement={undoRedoTooltipPlacement}>
        <button
          type="button"
          onClick={() => undo?.()}
          disabled={!canUndo}
          className={[
            "p-1.5 lg:p-1.5 text-white rounded-lg transition-colors",
            "hover:bg-white/10",
            "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent",
          ].join(" ")}
          aria-label="Undo"
        >
          <ArrowUturnLeftIcon className="w-4 h-4 lg:w-[18px] lg:h-[18px]" />
        </button>
      </HoverTooltip>

      <HoverTooltip label={`Redo (⌘/Ctrl+Shift+Z)`} placement={undoRedoTooltipPlacement}>
        <button
          type="button"
          onClick={() => redo?.()}
          disabled={!canRedo}
          className={[
            "p-1.5 lg:p-1.5 text-white rounded-lg transition-colors",
            "hover:bg-white/10",
            "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent",
          ].join(" ")}
          aria-label="Redo"
        >
          <ArrowUturnRightIcon className="w-4 h-4 lg:w-[18px] lg:h-[18px]" />
        </button>
      </HoverTooltip>

      {/* Format painter — sits next to Redo because it is conceptually a
          history/clipboard-style action (copy a style, then apply it). */}
      {includeFormatPainter ? renderFormatPainter() : null}
    </div>
  );

  const renderToolsCluster = ({ wrap = true, includeDevicePreview = true, includeFormatPainter = false } = {}) => (
    <>
      {/* Device preview controls */}
      {includeDevicePreview ? (
        <div data-tutorial-id="navbar-device-preview" className="flex items-center">
          <DevicePreviewControls
            mode={mode}
            setMode={setMode}
            orientation={orientation}
            setOrientation={setOrientation}
            showFrame={showFrame}
            setShowFrame={setShowFrame}
            forceCompact={useCompactViewControls}
          />
        </div>
      ) : null}

      {includeDevicePreview ? <div className="w-px h-6 bg-white/20 mx-0.5" /> : null}

      {includeFormatPainter ? (
        <>
          <div className="flex items-center">
            {renderFormatPainter()}
          </div>
          <div className="w-px h-6 bg-white/20 mx-0.5" />
        </>
      ) : null}

      <div
        data-tutorial-id="navbar-tools"
        className={[
          "flex items-center gap-0.5",
          wrap ? "flex-wrap justify-center" : "flex-nowrap",
        ].join(" ")}
      >
        {/* Animations */}
                  {websiteId && (
          <HoverTooltip label="Animations" placement={desktopTooltipPlacement}>
                      <div className="relative">
                        <button
                          onClick={handleToggleAnimationsPanel}
                          className={[
                  "p-1.5 lg:p-1.5 text-white rounded-lg transition-colors",
                            animationsPanelOpen
                              ? "bg-white/20"
                              : "hover:bg-white/10",
                          ].join(" ")}
                          aria-label="Website animations"
                          aria-pressed={animationsPanelOpen}
                        >
                <SparklesIcon className="w-4 h-4 lg:w-[18px] lg:h-[18px]" />
                        </button>
                      </div>
                    </HoverTooltip>
                  )}

                  {/* Website defaults */}
                  {websiteId && (
          <HoverTooltip label="Website defaults" placement={desktopTooltipPlacement}>
                      <div className="relative">
                        <button
                          onClick={() => setDefaultsModalOpen(true)}
                className="p-1.5 lg:p-1.5 text-white rounded-lg hover:bg-white/10 transition-colors"
                          aria-label="Edit website defaults"
                        >
                <PaintPaletteIcon className="w-4 h-4 lg:w-[18px] lg:h-[18px]" />
                        </button>
                      </div>
                    </HoverTooltip>
                  )}

                  {/* Page settings */}
        <HoverTooltip label="Page settings" placement={desktopTooltipPlacement}>
                    <div className="relative">
                      <button
                        onClick={() => {
                          setEditPanelView("elements");
                          if (!selectElement) return;
                          const payload = buildPageSelectionPayload();
                          if (payload) selectElement(payload);
                        }}
              className="p-1.5 lg:p-1.5 text-white rounded-lg hover:bg-white/10 transition-colors"
                        aria-label="Edit page properties"
                      >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 lg:w-[18px] lg:h-[18px]">
                          <g>
                            <path d="M19.93617,11.29787v5.65958a2,2,0,0,1-2,2H7.7234a2,2,0,0,1-2-2V4.02128a2,2,0,0,1,2-2H13.617" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="bevel" strokeWidth="1.5"/>
                            <path d="M16.72872,18.95745v1.20744a2,2,0,0,1-2,2H4.516a2,2,0,0,1-2-2V6.22872a2,2,0,0,1,2-2H5.7234" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="bevel" strokeWidth="1.5"/>
                            <path d="M19.29888,2.253a1,1,0,0,0-1.41422,0L11.88331,8.25437l.01054.01054L11.79438,11.26a.21258.21258,0,0,0,.22194.22193l2.99506-.09946.01054.01054,6.00135-6.00135a1,1,0,0,0,0-1.41422Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"/>
                          </g>
                        </svg>
                      </button>
                    </div>
                  </HoverTooltip>

                  {/* Navigation management */}
        <HoverTooltip label="Navigation" placement={desktopTooltipPlacement}>
                    <div className="relative">
                      <button
                        onClick={() => setNavManagementDropdownOpen(!navManagementDropdownOpen)}
              className="p-1.5 lg:p-1.5 text-white rounded-lg hover:bg-white/10 transition-colors"
                        aria-label="Manage navigation"
                      >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 lg:w-[18px] lg:h-[18px]">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <line x1="9" y1="3" x2="9" y2="21" />
                        </svg>
                      </button>
                      {navManagementDropdownOpen && (
                        <div className={`absolute left-1/2 -translate-x-1/2 mt-2 w-[min(20rem,calc(100vw-24px))] sm:w-80 ${surfaces.elevated} rounded-lg shadow-xl border ${borders.default}`} style={{ zIndex: Z_INDEX.DROPDOWN }}>
                          <NavigationManagement
                            hasNavbar={hasNavbar}
                            hasLeftSidebar={hasLeftSidebar}
                            hasRightSidebar={hasRightSidebar}
                            hasFooter={hasFooter}
                            websiteId={websiteId}
                            deleteConfirmation={deleteConfirmation}
                            setDeleteConfirmation={setDeleteConfirmation}
                            isManagingNav={isManagingNav}
                            navManagementError={navManagementError}
                            setNavManagementError={setNavManagementError}
                            setIsManagingNav={setIsManagingNav}
                            setNavManagementDropdownOpen={setNavManagementDropdownOpen}
                          />
                        </div>
                      )}
                    </div>
                  </HoverTooltip>

                  {/* Version management */}
        <HoverTooltip label="Versions" placement={desktopTooltipPlacement}>
                    <div className="relative">
                      <button
                        onClick={() => setVersionManagementDropdownOpen(!versionManagementDropdownOpen)}
              className="p-1.5 lg:p-1.5 text-white rounded-lg hover:bg-white/10 transition-colors"
                        aria-label="Website versions"
                      >
              <VersionIcon className="w-4 h-4 lg:w-[18px] lg:h-[18px]" />
                      </button>
                      {versionManagementDropdownOpen && (
                        <div
                          className={[
                            isCompactNav
                              ? "fixed left-1/2 top-32 -translate-x-1/2"
                              : "absolute left-1/2 -translate-x-1/2 mt-2",
                            `w-[min(20rem,calc(100vw-24px))] sm:w-80 ${surfaces.elevated}`,
                            `rounded-lg shadow-xl border ${borders.default}`,
                          ].join(" ")}
                          style={{ zIndex: Z_INDEX.DROPDOWN }}
                        >
                          <VersionManagement
                            website={website}
                            username={username}
                            domain={domain}
                            currentVersionSlug={version_slug}
                            setVersionManagementDropdownOpen={setVersionManagementDropdownOpen}
                            preferences={preferences}
                          />
                        </div>
                      )}
                    </div>
                  </HoverTooltip>

        <div className="w-px h-6 bg-white/20 mx-0.5" />

                  {/* Theme Toggle */}
        <HoverTooltip label="Theme" placement={desktopTooltipPlacement}>
                    <span className="inline-flex">
            <ThemeToggle
              className={[
                "p-1.5 lg:p-1.5",
                "hover:bg-white/10",
                "transition-colors",
                "rounded-lg"
              ].join(" ")}
              iconClassName="w-4 h-4 lg:w-[18px] lg:h-[18px] text-white"
              moonColor="white"
              sunColor="white"
              moonHoverFill=""
            />
                    </span>
                  </HoverTooltip>

                  {/* Feedback */}
        <HoverTooltip label="Feedback" placement={desktopTooltipPlacement}>
                    <span className="inline-flex">
                      <button
                        onClick={() => setFeedbackModalOpen(true)}
              className="p-1.5 lg:p-1.5 text-white rounded-lg hover:bg-white/10 transition-colors"
                        aria-label="Open feedback form"
                      >
              <FeedbackIcon size={isCompactNav ? "sm" : "md"} color="white" />
                      </button>
                    </span>
                  </HoverTooltip>

                  {SHOW_EDITOR_SETTINGS ? (
          <HoverTooltip label="Editor settings" placement={desktopTooltipPlacement}>
                      <span className="inline-flex">
                        <EditSettingsDropdown
                          groupViewMode={groupViewMode}
                          setGroupViewMode={setGroupViewMode}
                          dockLayout={dockLayout}
                          setDockLayout={setDockLayout}
                        />
                      </span>
                    </HoverTooltip>
                  ) : null}
                </div>
              </>
  );

  return (
    <>
    <WebsiteDefaultsModal
      isOpen={defaultsModalOpen}
      onClose={() => setDefaultsModalOpen(false)}
      websiteId={websiteId}
    />
    <FeedbackModal
      isOpen={feedbackModalOpen}
      onClose={() => setFeedbackModalOpen(false)}
    />
    <SubscriptionPlansModal
      isOpen={upgradeModalOpen}
      onClose={() => setUpgradeModalOpen(false)}
      notice={
        <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
          <div className={`${typography.h5} ${textColors.primary}`}>Upgrade required to publish</div>
          <div className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
            Your current plan allows up to{" "}
            <span className={`${textColors.primary} font-medium`}>
              {publishLimitInfo?.max_published_websites ?? "0"}
            </span>{" "}
            published website{Number(publishLimitInfo?.max_published_websites || 0) === 1 ? "" : "s"}.
          </div>
        </div>
      }
    />
    <nav className={`sticky top-0 z-50 w-full bg-gradient-to-r from-blue-600 via-sky-500 to-blue-600 dark:from-dark-950 dark:via-dark-900 dark:to-dark-950 text-white border-b ${borders.glass} shadow-sm relative`}>
      <div className="w-full px-3 sm:px-4" ref={toolsPanelRef}>
        {/* ── Single main row ── */}
        <div
          className={
            isCompactNav
              ? "flex items-center h-12 gap-1.5"
              : "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center h-16 gap-3"
          }
        >
          {/* Left: Home + Tutorial + (desktop) website info */}
          <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
            <div data-tutorial-id="navbar-home-tutorial" className="flex items-center gap-0.5 flex-shrink-0">
              <Link
                to={`/edit/${username}`}
                className={`p-1.5 hover:bg-white/10 rounded-lg transition-colors flex items-center justify-center`}
                title="Dashboard Home"
              >
                <HomeIcon size={isCompactNav ? "md" : "md"} color="white" />
              </Link>
              <HoverTooltip label="Tutorial" placement="bottom">
                <button
                  onClick={() => startTutorial()}
                  className={`p-1.5 text-white rounded-lg hover:bg-white/10 transition-colors`}
                  aria-label="Open editor tutorial"
                >
                  <InfoIcon size={isCompactNav ? "sm" : "md"} color="white" />
                </button>
              </HoverTooltip>
            </div>

            {showWebsiteIdentity ? (
              <>
                {/* Separator between tutorial controls and website identity */}
                <div className="w-px h-6 bg-white/20 mx-1" aria-hidden="true" />

                <div className="flex flex-col leading-tight min-w-0 overflow-hidden">
                  <div className={`${typography.h4} text-white truncate max-w-72 font-bold`}>
                    {website?.name || domain}
                  </div>
                  <div className={`${typography.bodySmall} text-white/80 truncate max-w-72`}>
                    Page: {currentPage?.name || currentPage?.subdomain || "Home"} • v
                    {website?.version_name || formatVersionName(version_slug)} •{" "}
                    {formatLastEditedTime(website?.updated_at, preferences.timezone)}
                  </div>
                </div>

                {/* Undo / Redo (separate section to the right of the website name) */}
                <div className="flex items-center flex-shrink-0">
                  <div className="w-px h-6 bg-white/20 mx-1.5" aria-hidden="true" />
                  {renderUndoRedo()}
                </div>
              </>
            ) : null}

            {/* Narrow desktop: hide website name/info, but keep Undo/Redo accessible */}
            {!isCompactNav && hideWebsiteIdentity ? (
              <div className="flex items-center flex-shrink-0">
                <div className="w-px h-6 bg-white/20 mx-1.5" aria-hidden="true" />
                {renderUndoRedo()}
              </div>
            ) : null}

            {/* Compact nav: website name isn't shown on the left, so keep undo/redo next to Home/Tutorial */}
            {isCompactNav ? (
              <div className="flex items-center flex-shrink-0">
                {renderUndoRedo({ includeFormatPainter: false })}
              </div>
            ) : null}
          </div>

          {/* Center: Page navigation */}
          <div
            data-tutorial-id="navbar-page-controls"
            className={isCompactNav ? "flex-1 flex justify-center min-w-0" : "justify-self-center min-w-0"}
          >
            <PageNavigationEditor
              currentPage={currentPage}
              pages={pages}
              website={website}
              websiteId={websiteId}
              username={username}
              domain={domain}
              version_slug={version_slug}
              generateEditUrl={generateEditUrl}
              createPageData={createPageData}
              setCreatePageData={setCreatePageData}
              createPageError={createPageError}
              setCreatePageError={setCreatePageError}
              isCreatingPage={isCreatingPage}
              hasNavbar={hasNavbar}
              hasLeftSidebar={hasLeftSidebar}
              hasRightSidebar={hasRightSidebar}
              onCreatePageSubmit={handleCreatePageSubmit}
              onCreatePageCancel={handleCreatePageCancel}
              // Keep the page selector "full" at `xl`+ even if the navbar is compact (e.g. 1280-1535px).
              // Only force the simplified selector below `xl`.
              forceSimplifiedLayout={isCompactNav && !forceFullPageNav}
              forceFullLayout={forceFullPageNav}
              compactControls={isCompactNav}
            />
          </div>

          {/* Right: three-dots menu + save icon + publish */}
          <div className={isCompactNav ? "flex items-center gap-1.5 flex-shrink-0" : "flex items-center gap-2 justify-self-end"}>
            {isCompactNav ? (
              <>
                <button
                  onClick={() => {
                    // During tutorial steps that target items inside the tools panel, keep it expanded
                    // so coachmarks always have a real DOM anchor.
                    if (tutorialWantsToolsExpanded) {
                      setToolsExpanded(true);
                      return;
                    }
                    setToolsExpanded((prev) => !prev);
                  }}
                  className={[
                    "p-1 text-white rounded-lg transition-colors sm:p-1.5",
                    toolsExpanded
                      ? "bg-white/20"
                      : "hover:bg-white/10",
                  ].join(" ")}
                  aria-label="Toggle tools menu"
                  aria-expanded={toolsExpanded}
                  title="More tools"
                >
                  <SettingsDotsIcon size="sm" color="white" />
                </button>
              </>
            ) : (
              <div className="flex items-center gap-1.5">
                {renderToolsCluster({ wrap: false })}
              </div>
            )}

            <div className={isCompactNav ? "flex items-center gap-1.5" : "flex items-center gap-3"}>
              <HoverTooltip
                label={
                  saveStatus === "saving"
                    ? "Saving…"
                    : saveStatus === "error"
                      ? "Error"
                      : saveStatus === "conflict"
                        ? "Conflict"
                        : "Saved"
                }
                placement="bottom"
              >
                <span className="inline-flex">
                  <SaveIndicator status={saveStatus} showLabel={!isCompactNav} />
                </span>
              </HoverTooltip>

              <span data-tutorial-id="navbar-publish" className="inline-flex">
                <PublishButton
                  onPublish={handlePublish}
                  disabled={saveStatus === "saving"}
                  publishConfirmOpen={publishConfirmOpen}
                  setPublishConfirmOpen={setPublishConfirmOpen}
                  onConfirmPublish={confirmPublish}
                  publishing={publishing}
                />
              </span>
            </div>
          </div>
        </div>

        {/* ── Expandable tools panel ── */}
        {isCompactNav && toolsExpanded && (
          <div className="border-t border-white/20 pb-3 pt-2 animate-in slide-in-from-top-1 duration-150">
            {/* Website info row */}
            <div className="flex items-center gap-2 px-2 pb-2 mb-2 border-b border-white/20">
              <div className="flex flex-col min-w-0 flex-1">
                <div className={`${typography.bodySmall} text-white font-medium truncate`}>
                  {website?.name || domain}
                </div>
                <div className="text-[11px] text-white/80 flex items-center gap-1 flex-wrap">
                  {currentPage && (
                    <span className="truncate max-w-32">
                      Page: {currentPage.name || "Home"}
                    </span>
                  )}
                  <span>• v{website?.version_name || formatVersionName(version_slug)}</span>
                  <span>• {formatLastEditedTime(website?.updated_at, preferences.timezone)}</span>
                </div>
              </div>
              {hasPublished && (website?.domain || domain) && (
                <a
                  href={`${APP_URL_WWW}/${username}/${website?.domain || domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white flex items-center gap-1 text-xs flex-shrink-0"
                  title="Open live website"
                >
                  <ExternalLinkIcon size="xs" />
                  <span className="hidden sm:inline">Open site</span>
                </a>
              )}
            </div>

            {/* Tools row */}
            <div className="flex flex-wrap items-center justify-center gap-1 px-1">
              {renderToolsCluster({ wrap: true, includeDevicePreview: true, includeFormatPainter: true })}
            </div>
          </div>
        )}
      </div>

      {showTutorialPrompt ? (
        <div
          data-testid="tutorial-start-prompt"
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3"
          style={{ zIndex: Z_INDEX.POPOVER }}
        >
          <TutorialStartPrompt
            onStart={() => {
              dismissTutorialPrompt();
              startTutorial();
            }}
            onSkip={dismissTutorialPrompt}
          />
        </div>
      ) : null}

      {mobileNoticeOpen ? (
        <div
          data-testid="mobile-editor-notice"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-editor-notice-title"
          aria-describedby="mobile-editor-notice-body"
          className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          style={{ zIndex: Z_INDEX.MODAL ?? 70 }}
        >
          <div
            className={[
              "w-full max-w-md rounded-2xl border shadow-2xl p-6",
              surfaces.elevated,
              borders.default,
            ].join(" ")}
          >
            <div className="flex justify-center mb-4" aria-hidden="true">
              <span
                className={[
                  "h-14 w-14 rounded-full inline-flex items-center justify-center",
                  "bg-blue-50 dark:bg-blue-900/30",
                  "text-blue-600 dark:text-blue-400",
                ].join(" ")}
              >
                <ComputerDesktopIcon className="w-7 h-7" />
              </span>
            </div>

            <h2
              id="mobile-editor-notice-title"
              className={`${typography.h4} ${textColors.primary} text-center`}
            >
              Best edited on desktop or iPad
            </h2>

            <p
              id="mobile-editor-notice-body"
              className={`${typography.bodySmall} ${textColors.secondary} text-center mt-2`}
            >
              We&apos;re still polishing mobile editing.
            </p>

            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={dismissMobileNotice}
                className={[
                  "px-5 py-2 rounded-md",
                  typography.button,
                  interactive.primaryBg,
                  interactive.primaryHover,
                  interactive.primaryText,
                ].join(" ")}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}
      
      {/* Click outside to close dropdowns */}
      {(navManagementDropdownOpen || versionManagementDropdownOpen) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setNavManagementDropdownOpen(false);
            setVersionManagementDropdownOpen(false);
          }}
        />
      )}
    </nav>
    </>
  );
};

export default EditNavBar;
